import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, BehaviorSubject, shareReplay, tap } from 'rxjs';
import { IProfessional } from '../interfaces/professional/iprofessional';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ProfessionalService {
  filter !: IProfessionalFilter;
  api = environment.url;

  filterQueryParams: string = "";
  private readonly getAllCache = new Map<string, Observable<IProfessional[]>>();

  public totalItemsSubject = new BehaviorSubject<number>(0);

  constructor(private http: HttpClient) {}

  getAll(filter: string = "", totalPages: string = "") {
    let queryParams: any;
    if(filter.length <= 0) {
      let urlParams = new URLSearchParams();
      if(this.filter?.profession) {
        this.filter.profession.forEach(id => {
          urlParams.append('profession',id);
        });
      }
      if(this.filter?.unit) {
        this.filter.unit.forEach(id => {
          urlParams.append('units',id);
        });
      }
      if(this.filter?.name) {
        urlParams.append('name',this.filter.name);
      }

      queryParams = urlParams.toString();
    } else {
      queryParams = filter;
    }

    if(this.filterQueryParams.length > 0) {
      queryParams = this.filterQueryParams;
    }

    const itemsPerPage = totalPages || '1000';

    const cacheKey = `${queryParams}|itemsPerPage=${itemsPerPage}`;
    const cached = this.getAllCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request$ = this.http
      .get(`${this.api}/professionals${queryParams}`, { params: { itemsPerPage: itemsPerPage }})
      .pipe(
        map((res: any) => {
          this.totalItemsSubject.next(res['hydra:totalItems'] ?? 0);
          return res['hydra:member'];
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      ) as Observable<IProfessional[]>;

    this.getAllCache.set(cacheKey, request$);
    return request$;
  }

  getById(id: number) {
    return this.http
      .get(`${this.api}/professionals/${id}`) as Observable<IProfessional>;
  }

  getLegalStructures(){
    return this.http
    .get(`${this.api}/legal_structures`).pipe(map((res: any) => res['hydra:member'])) as Observable<IProfessional[]>;

  }

  save(req: IProfessional) {
    return this.http.post<IProfessional>(
      `${environment.url}/professionals`,
      req
    ).pipe(tap(() => this.clearGetAllCache()));
  }


  edit(req: IProfessional, id: number) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/merge-patch+json',
    });
    return this.http.patch<IProfessional>(
      `${environment.url}/professionals/${id}`,
      req,
      { headers: headers }
    ).pipe(tap(() => this.clearGetAllCache()));
  }

  delete(id: number) {
    return this.http.delete<IProfessional>(
      `${environment.url}/professionals/${id}`
    ).pipe(tap(() => this.clearGetAllCache()));
  }

  setFilter(filter : IProfessionalFilter) {
    this.filter = filter;
  }

  setFilterQueryParams(filterQuqeryParams: string) {
    this.filterQueryParams = filterQuqeryParams;
  }

  clearFilterQueryParams() {
    this.setFilterQueryParams('');
  }

  private clearGetAllCache(): void {
    this.getAllCache.clear();
  }

  sendEmail( id: number, type: 'unit' | 'professional' ){

    let req = {
      type: type,
      id : id
    }

    return this.http.post(
      `${environment.url}/text/send-proof-life-link`,
      req
    ) as Observable<any>;
  }

}

export interface IProfessionalFilter {
  profession : string [],
  unit: string[] ,
  name: string
}

export class ProfessionalFilter {
  profession !: string [];
  unit!: string[];
  name! : string;

  page !: string;
}
